export interface CertificateData {
  recipientName: string;
  courseTitle: string;
  completionDate: string;
  credentialId: string;
  issuer: string;
}

export function generateCertificateHTML(data: CertificateData): string {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>${data.courseTitle} Certificate – ${data.recipientName}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;600;700&family=DM+Serif+Display&display=swap');
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'DM Sans', sans-serif;
      background: #0a0f1a;
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      padding: 40px;
    }
    .cert {
      width: 900px;
      background: linear-gradient(135deg, #0d1424 0%, #111827 50%, #0d1424 100%);
      border: 1px solid rgba(99,179,237,0.25);
      border-radius: 24px;
      padding: 64px 72px;
      position: relative;
      overflow: hidden;
      box-shadow: 0 0 80px rgba(99,179,237,0.08);
    }
    .cert::before {
      content: '';
      position: absolute;
      top: -120px; left: -120px;
      width: 400px; height: 400px;
      background: radial-gradient(circle, rgba(99,179,237,0.12) 0%, transparent 70%);
      pointer-events: none;
    }
    .cert::after {
      content: '';
      position: absolute;
      bottom: -120px; right: -120px;
      width: 400px; height: 400px;
      background: radial-gradient(circle, rgba(245,158,11,0.10) 0%, transparent 70%);
      pointer-events: none;
    }
    .border-deco {
      position: absolute;
      inset: 16px;
      border: 1px solid rgba(99,179,237,0.12);
      border-radius: 16px;
      pointer-events: none;
    }
    .logo-row {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 48px;
    }
    .logo-icon {
      width: 44px; height: 44px;
      background: linear-gradient(135deg, #63b3ed, #f59e0b);
      border-radius: 12px;
      display: flex; align-items: center; justify-content: center;
      font-size: 22px;
    }
    .logo-text {
      font-size: 15px;
      font-weight: 700;
      color: rgba(255,255,255,0.9);
      letter-spacing: 0.02em;
    }
    .cert-label {
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      color: #63b3ed;
      margin-bottom: 16px;
    }
    .cert-title {
      font-family: 'DM Serif Display', serif;
      font-size: 42px;
      color: #ffffff;
      line-height: 1.15;
      margin-bottom: 32px;
    }
    .presented-to {
      font-size: 13px;
      color: rgba(255,255,255,0.45);
      letter-spacing: 0.06em;
      text-transform: uppercase;
      margin-bottom: 12px;
    }
    .recipient-name {
      font-family: 'DM Serif Display', serif;
      font-size: 52px;
      color: #f59e0b;
      margin-bottom: 28px;
      line-height: 1.1;
    }
    .completion-text {
      font-size: 15px;
      color: rgba(255,255,255,0.55);
      line-height: 1.7;
      max-width: 560px;
      margin-bottom: 48px;
    }
    .completion-text strong {
      color: rgba(255,255,255,0.85);
      font-weight: 600;
    }
    .divider {
      width: 80px;
      height: 2px;
      background: linear-gradient(90deg, #63b3ed, #f59e0b);
      border-radius: 2px;
      margin-bottom: 40px;
    }
    .footer-row {
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      gap: 24px;
    }
    .sig-block { flex: 1; }
    .sig-line {
      width: 160px;
      height: 1px;
      background: rgba(255,255,255,0.2);
      margin-bottom: 8px;
    }
    .sig-name {
      font-size: 13px;
      font-weight: 600;
      color: rgba(255,255,255,0.7);
    }
    .sig-title {
      font-size: 11px;
      color: rgba(255,255,255,0.35);
      margin-top: 2px;
    }
    .credential-block {
      text-align: right;
    }
    .credential-label {
      font-size: 10px;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: rgba(255,255,255,0.3);
      margin-bottom: 4px;
    }
    .credential-id {
      font-family: 'Courier New', monospace;
      font-size: 12px;
      color: rgba(99,179,237,0.7);
    }
    .credential-date {
      font-size: 12px;
      color: rgba(255,255,255,0.4);
      margin-top: 4px;
    }
    .badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 14px;
      border-radius: 999px;
      border: 1px solid rgba(99,179,237,0.3);
      background: rgba(99,179,237,0.08);
      font-size: 11px;
      font-weight: 600;
      color: #63b3ed;
      letter-spacing: 0.04em;
      margin-bottom: 32px;
    }
    @media print {
      body { background: white; padding: 0; }
      .cert { box-shadow: none; }
    }
  </style>
</head>
<body>
  <div class="cert">
    <div class="border-deco"></div>
    <div class="logo-row">
      <div class="logo-icon">🎓</div>
      <span class="logo-text">Free Crypto Academy</span>
    </div>
    <div class="cert-label">Certificate of Completion</div>
    <div class="cert-title">${data.courseTitle}</div>
    <div class="badge">✓ Verified Achievement</div>
    <div class="presented-to">This certificate is proudly presented to</div>
    <div class="recipient-name">${data.recipientName}</div>
    <div class="divider"></div>
    <div class="completion-text">
      for successfully completing the <strong>${data.courseTitle}</strong> course track at Free Crypto Academy,
      demonstrating commitment to professional development and mastery of the curriculum.
    </div>
    <div class="footer-row">
      <div class="sig-block">
        <div class="sig-line"></div>
        <div class="sig-name">${data.issuer}</div>
        <div class="sig-title">Free Crypto Academy</div>
      </div>
      <div class="credential-block">
        <div class="credential-label">Credential ID</div>
        <div class="credential-id">${data.credentialId}</div>
        <div class="credential-date">Issued ${data.completionDate}</div>
      </div>
    </div>
  </div>
</body>
</html>`;
}

export function downloadCertificate(data: CertificateData): void {
  const html = generateCertificateHTML(data);
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, '_blank');
  if (win) {
    win.onload = () => {
      setTimeout(() => {
        win.print();
      }, 500);
    };
  }
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

export function generateCredentialId(courseId: string): string {
  const prefix = courseId.slice(0, 2).toUpperCase();
  const year = new Date().getFullYear();
  const rand = Math.floor(10000 + Math.random() * 90000);
  return `FCA-${prefix}-${year}-${rand}`;
}
